import { useEffect, useState } from "react";
import api, { fmtDate } from "@/lib/api";

const blank = () => ({ project_id: "", stage_id: "", task: "", start_time: "", end_time: "", hours: 0, notes: "" });

function Line({ en, i, projects, stages, set, remove }) {
  const st = stages[en.project_id] || [];
  return (
    <div className="grid grid-cols-2 md:grid-cols-8 gap-2 items-center border-b border-neutral-800 pb-2" data-testid={`ats-line-${i}`}>
      <select className="input md:col-span-2" value={en.project_id} onChange={e => set(i, { project_id: e.target.value, stage_id: "" })} data-testid={`ats-project-${i}`}>
        <option value="">— Project —</option>
        {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      <select className="input" value={en.stage_id || ""} onChange={e => set(i, { stage_id: e.target.value })} data-testid={`ats-stage-${i}`}>
        <option value="">— Stage —</option>
        {st.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
      </select>
      <input className="input" placeholder="Task" value={en.task || ""} onChange={e => set(i, { task: e.target.value })} data-testid={`ats-task-${i}`} />
      <input type="time" className="input" value={en.start_time || ""} onChange={e => set(i, { start_time: e.target.value })} data-testid={`ats-start-${i}`} aria-label="Start" />
      <input type="time" className="input" value={en.end_time || ""} onChange={e => set(i, { end_time: e.target.value })} data-testid={`ats-end-${i}`} aria-label="End" />
      <input type="number" step="0.25" min="0" className="input" disabled={!!(en.start_time && en.end_time)} value={en.hours} onChange={e => set(i, { hours: e.target.value })} data-testid={`ats-hours-${i}`} aria-label="Hours" />
      <div className="flex gap-2 col-span-2 md:col-span-8">
        <input className="input flex-1" placeholder="Notes" value={en.notes || ""} onChange={e => set(i, { notes: e.target.value })} data-testid={`ats-notes-${i}`} />
        <button type="button" className="btn-ghost text-xs text-red-400" onClick={() => remove(i)} data-testid={`ats-remove-${i}`}>Remove line</button>
      </div>
    </div>
  );
}

function AuditList({ tid, refresh }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get(`/timesheets/${tid}/audit`).then(r => setRows(r.data)).catch(() => {}); }, [tid, refresh]);
  if (!rows.length) return <div className="text-xs opacity-50">No admin edits yet.</div>;
  const sum = s => s ? `${fmtDate(s.work_date)} · ${s.total_hours} h · break ${s.break_taken ? "yes" : "no"} · ${(s.entries || []).map(e => `${e.project_name || "—"} ${e.start_time || ""}${e.end_time ? "–" + e.end_time : ""} ${e.hours}h${e.task ? " " + e.task : ""}${e.notes ? ` "${e.notes}"` : ""}`).join("; ")}` : "deleted";
  return (
    <ul className="text-xs space-y-1" data-testid="ats-audit">
      {rows.map(r => <li key={r.id}><b>{fmtDate(r.at)} {new Date(r.at).toLocaleTimeString("en-NZ", { hour: "2-digit", minute: "2-digit" })} · {r.by} · {r.action}</b><div className="opacity-70">Before: {sum(r.before)}</div><div className="opacity-70">After: {sum(r.after)}</div></li>)}
    </ul>
  );
}

export function AdminTsEditor({ ts, onClose }) {
  const [date, setDate] = useState(ts.work_date?.slice(0, 10) || "");
  const [breakTaken, setBreakTaken] = useState(!!ts.break_taken);
  const [entries, setEntries] = useState((ts.entries || []).map(e => ({ ...blank(), ...e })));
  const [projects, setProjects] = useState([]);
  const [stages, setStages] = useState({});
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/projects").then(r => setProjects(r.data)); }, []);
  useEffect(() => {
    [...new Set(entries.map(e => e.project_id).filter(Boolean))].filter(p => !stages[p]).forEach(pid =>
      api.get(`/projects/${pid}/stages`).then(r => setStages(s => ({ ...s, [pid]: r.data }))).catch(() => {}));
  }, [entries, stages]);
  const set = (i, patch) => setEntries(es => es.map((e, j) => (j === i ? { ...e, ...patch } : e)));
  const remove = i => setEntries(es => es.filter((_, j) => j !== i));

  async function save() {
    setBusy(true); setErr("");
    try {
      await api.put(`/timesheets/${ts.id}`, { work_date: date, break_taken: breakTaken,
        entries: entries.map(({ project_id, stage_id, task, start_time, end_time, hours, notes, task_id, programme_item_id }) =>
          ({ project_id, stage_id: stage_id || null, task, task_id, programme_item_id, start_time: start_time || null, end_time: end_time || null, hours: Number(hours) || 0, notes })) });
      onClose(true);
    } catch (e) { setErr(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Couldn't save"); setBusy(false); }
  }
  async function del() {
    if ((window.prompt(`Delete ${ts.worker_name}'s timesheet for ${fmtDate(ts.work_date)}? This is logged.\n\nType DELETE to confirm.`) || "").trim() !== "DELETE") return;
    try { await api.delete(`/timesheets/${ts.id}`); onClose(true); }
    catch (e) { setErr(e?.response?.data?.detail || "Couldn't delete"); }
  }

  return (
    <div className="card space-y-3" data-testid="admin-ts-editor">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-silver">Edit timesheet — {ts.worker_name} <span className="badge badge-grey ml-1">{ts.status === "Accepted" ? "Approved" : ts.status}</span></h3>
        <button className="btn-ghost text-sm" onClick={() => onClose(false)} data-testid="ats-close">Close</button>
      </div>
      <div className="flex gap-3 flex-wrap items-center">
        <label className="text-sm">Date <input type="date" className="input inline-block w-44 ml-1" value={date} onChange={e => setDate(e.target.value)} data-testid="ats-date" /></label>
        <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={breakTaken} onChange={e => setBreakTaken(e.target.checked)} data-testid="ats-break" />Unpaid break taken</label>
      </div>
      {entries.map((en, i) => <Line key={i} en={en} i={i} projects={projects} stages={stages} set={set} remove={remove} />)}
      <button type="button" className="btn-ghost text-sm" onClick={() => setEntries(es => [...es, blank()])} data-testid="ats-add-line">+ Add line</button>
      {err && <div className="text-sm text-red-400" data-testid="ats-error">{err}</div>}
      <div className="flex gap-2 flex-wrap">
        <button className="btn-silver" disabled={busy || !entries.length} onClick={save} data-testid="ats-save">Save changes</button>
        <button className="btn-ghost text-red-400" onClick={del} data-testid="ats-delete">Delete timesheet</button>
      </div>
      <div><div className="text-xs text-silver mb-1">Admin edit history</div><AuditList tid={ts.id} refresh={ts.updated_at} /></div>
    </div>
  );
}
